"use client";

/**
 * Full assumptions spreadsheet table.
 *
 * Extracted from `compass-assumptions.jsx:145-197` (`.asm-table`).
 */

import { useMemo } from "react";
import { InfoDot } from "../data-display/InfoDot";
import { ProvenanceDot } from "../data-display/ProvenanceDot";
import { Clock, Pen } from "../lib/icons";
import type { ProvenanceType } from "../lib/provenance";

export interface AssumptionMetricLink {
  id: string;
  label: string;
  unit?: string | null;
  direction?: string | null;
}

function metricMetadata(metric: AssumptionMetricLink): string {
  return [metric.unit, metric.direction].filter(Boolean).join(" · ");
}

function metricSignature(metric: AssumptionMetricLink): string {
  return `${metric.label}\u0000${metricMetadata(metric)}`;
}

interface MetricChipMetadata {
  metricLabelCounts: Map<string, number>;
  ordinalById: Map<string, number>;
}

const EMPTY_METRIC_CHIP_METADATA: MetricChipMetadata = {
  metricLabelCounts: new Map(),
  ordinalById: new Map(),
};

function buildMetricChipMetadata(
  metrics: AssumptionMetricLink[] | undefined,
): MetricChipMetadata {
  const metricLabelCounts = new Map<string, number>();
  const idsBySignature = new Map<string, string[]>();

  for (const metric of metrics ?? []) {
    metricLabelCounts.set(
      metric.label,
      (metricLabelCounts.get(metric.label) ?? 0) + 1,
    );
    const signature = metricSignature(metric);
    const ids = idsBySignature.get(signature) ?? [];
    ids.push(metric.id);
    idsBySignature.set(signature, ids);
  }

  const ordinalById = new Map<string, number>();
  for (const ids of idsBySignature.values()) {
    if (ids.length < 2) continue;
    for (const [index, id] of [...ids].sort().entries()) {
      ordinalById.set(id, index + 1);
    }
  }

  return { metricLabelCounts, ordinalById };
}

export interface AssumptionRow {
  id: string;
  label: string;
  value?: string;
  type: ProvenanceType;
  description?: string;
  by?: string;
  history?: number;
  org?: boolean;
  empty?: boolean;
  /** Parent metrics this assumption feeds; shown as chips. Empty/absent when
   * the assumption feeds a cost/ungrouped stream or a legacy model. */
  metrics?: AssumptionMetricLink[];
  /** Defaults to true; false hides and disables value edit controls. */
  editable?: boolean;
  /** User has confirmed this value (trust marker; assumptions only). */
  confirmed?: boolean;
  /** Page-level section membership; not rendered by the table itself. */
  streamDirections?: Array<"benefit" | "cost">;
}

export interface AssumptionsTableProps {
  rows: AssumptionRow[];
  activeId?: string;
  edits?: Record<string, boolean>;
  onOpenEdit?: (id: string) => void;
  onOpenSource?: (id: string) => void;
  onOpenHistory?: (id: string) => void;
}

export function AssumptionsTable({
  rows,
  activeId,
  edits,
  onOpenEdit,
  onOpenSource,
  onOpenHistory,
}: AssumptionsTableProps) {
  const metricChipMetadataByRowId = useMemo(
    () =>
      new Map(
        rows.map((row) => [row.id, buildMetricChipMetadata(row.metrics)]),
      ),
    [rows],
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr className="border-b border-(--border-subtle)">
            <th className="text-left py-2 px-3 text-[11px] tracking-[.06em] uppercase text-(--fg-muted) font-semibold">
              Assumption
            </th>
            <th className="text-left py-2 px-3 text-[11px] tracking-[.06em] uppercase text-(--fg-muted) font-semibold">
              Value
            </th>
            <th className="text-left py-2 px-3 text-[11px] tracking-[.06em] uppercase text-(--fg-muted) font-semibold">
              Source / History
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            // Two distinct metrics can share a label — research dedupes
            // candidate IDs with a numeric suffix but leaves labels identical.
            // Disambiguate with unit/direction metadata, and when even that
            // matches, with a positional ordinal. NOT with `metric.id`, which
            // is an internal slug ("time-from-detection-to-escalation") and
            // reads as leaked plumbing; the id stays available to automation
            // via `data-metric-id`.
            const { metricLabelCounts, ordinalById } =
              metricChipMetadataByRowId.get(r.id) ?? EMPTY_METRIC_CHIP_METADATA;
            const isEdited = edits?.[r.id];
            const isEmpty = r.empty && !isEdited;
            const sel = activeId === r.id;
            const et: ProvenanceType = isEdited ? "provided" : r.type;
            const revs = (r.history ?? 0) + (isEdited ? 1 : 0);
            const editable = r.editable !== false;
            const canOpenEdit = editable && onOpenEdit != null;
            // Source and history are context for every assumption, including
            // read-only organization values and values that still need input.
            const canOpenSource = onOpenSource != null;
            const sourceText = isEdited
              ? "by you"
              : isEmpty
                ? "Research compiled"
                : r.type === "provided"
                  ? r.by
                    ? `by ${r.by}`
                    : "Provided"
                  : (r.by ?? "Estimated");
            const valueContent = (
              <>
                <span
                  className={[
                    "text-[15px] font-bold",
                    isEmpty ? "text-(--decide)" : "text-(--fg-primary)",
                  ].join(" ")}
                >
                  {isEmpty ? "Needs input" : r.value}
                </span>
                {canOpenEdit && (
                  <span className="text-(--fg-muted) hover:text-(--fg-primary)">
                    <Pen size={16} />
                  </span>
                )}
                <span className="ml-auto flex items-center gap-2">
                  {isEdited && (
                    <span className="text-[11px] font-semibold text-(--prov) bg-(--prov-bg) rounded-(--radius-xs) px-[9px] py-[5px]">
                      Edited
                    </span>
                  )}
                  {r.org && !isEdited && (
                    <span className="text-[11px] font-semibold text-(--prov) bg-(--prov-bg) rounded-(--radius-xs) px-[9px] py-[5px]">
                      Org
                    </span>
                  )}
                  <ProvenanceDot type={et} />
                </span>
              </>
            );
            const sourceContent = (
              <>
                <span className="text-[13px] text-(--fg-secondary)">
                  {sourceText}
                </span>
                {r.confirmed && (
                  <span className="text-[11px] font-semibold text-(--prov) bg-(--prov-bg) rounded-(--radius-xs) px-[9px] py-[5px]">
                    Confirmed
                  </span>
                )}
              </>
            );

            return (
              <tr
                key={r.id}
                className={[
                  "border-b border-(--border-subtle)",
                  sel ? "bg-(--bg-sky)" : "",
                  isEmpty ? "bg-(--decide-bg)" : "",
                  isEmpty ? "hover:bg-(--decide-bg)" : "hover:bg-(--bg-canvas)",
                ].join(" ")}
              >
                {/* Assumption name + info */}
                <td className="py-4 px-3 align-middle">
                  <div className="flex flex-col">
                    <span>
                      <span className="text-[14px] font-medium text-(--fg-primary)">
                        {r.label}
                      </span>
                      {r.description && (
                        <span className="ml-1.5 inline-flex">
                          <InfoDot text={r.description} />
                        </span>
                      )}
                    </span>
                    {r.metrics && r.metrics.length > 0 && (
                      <ul
                        className="mt-1 mb-0 flex list-none flex-wrap gap-1 p-0"
                        aria-label={`${r.label} feeds these metrics`}
                      >
                        {r.metrics.map((metric) => {
                          const metadata = metricMetadata(metric);
                          const ordinal = ordinalById.get(metric.id);
                          const duplicateLabel =
                            (metricLabelCounts.get(metric.label) ?? 0) > 1;
                          const disambiguator = duplicateLabel
                            ? [metadata, ordinal ? `#${ordinal}` : ""]
                                .filter(Boolean)
                                .join(" · ")
                            : "";
                          return (
                            <li
                              key={metric.id}
                              data-metric-id={metric.id}
                              aria-label={
                                disambiguator
                                  ? `${metric.label} (${disambiguator})`
                                  : metric.label
                              }
                              className="inline-flex w-fit items-center rounded-(--radius-xs) bg-(--bg-sky) px-1.5 py-0.5 text-[11px] font-medium text-(--fg-brand)"
                            >
                              {metric.label}
                              {disambiguator ? ` · ${disambiguator}` : null}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </td>

                {/* Value + pen + provenance badges */}
                <td className="py-4 px-3 align-middle">
                  {canOpenEdit ? (
                    <button
                      type="button"
                      aria-label={`Edit value for ${r.label}`}
                      className="flex w-full items-center gap-2 border-0 bg-transparent p-0 text-left font-[inherit] cursor-pointer"
                      onClick={() => onOpenEdit(r.id)}
                    >
                      {valueContent}
                    </button>
                  ) : (
                    <span className="flex items-center gap-2">
                      {valueContent}
                    </span>
                  )}
                </td>

                {/* Source / History */}
                <td
                  className={[
                    "py-4 px-3 align-middle",
                    canOpenSource ? "cursor-pointer" : "",
                  ].join(" ")}
                  onClick={canOpenSource ? () => onOpenSource(r.id) : undefined}
                  onKeyDown={
                    canOpenSource
                      ? (event) => {
                          // Ignore keydowns that bubbled up from a nested
                          // button (e.g. "View history") — only handle a key
                          // pressed on the cell itself.
                          if (event.target !== event.currentTarget) return;
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            onOpenSource(r.id);
                          }
                        }
                      : undefined
                  }
                  tabIndex={canOpenSource ? 0 : undefined}
                >
                  <span className="flex items-center gap-2">
                    {canOpenSource ? (
                      <button
                        type="button"
                        aria-label={`View source for ${r.label}`}
                        className="inline-flex min-w-0 flex-1 items-center gap-2 border-0 bg-transparent p-0 text-left font-[inherit] cursor-pointer"
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenSource(r.id);
                        }}
                      >
                        {sourceContent}
                      </button>
                    ) : (
                      <span className="inline-flex min-w-0 flex-1 items-center gap-2">
                        {sourceContent}
                      </span>
                    )}
                    {onOpenHistory && (
                      <button
                        type="button"
                        aria-label="View history"
                        className={[
                          "ml-auto inline-flex items-center gap-1",
                          "bg-transparent border-0 cursor-pointer p-0",
                          revs > 0
                            ? "text-(--fg-secondary)"
                            : "text-(--fg-muted)",
                        ].join(" ")}
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenHistory(r.id);
                        }}
                        title="View history"
                      >
                        <Clock size={16} />
                        {revs > 0 && (
                          <span className="text-[11px] font-semibold">
                            {revs}
                          </span>
                        )}
                      </button>
                    )}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
